import {
  CheckIcon,
  ChevronRightIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { Helmet } from 'react-helmet-async';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import MoreIcon from '../../../assets/icons/more-horizontal-icon.svg';
import AddInsurancePlansModal from '../../../components/add-insurance-plans-modal/AddInsurancePlansModal';
import CustomFieldBadge from '../../../components/customfield-badge/CustomFieldBadge';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import DailyAlertsCard from '../../../components/daily-alerts-card/DailyAlertsCard';
import DirectionsButton from '../../../components/directions-btn/DirectionsButton';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import OfficeNotesCard from '../../../components/office-notes-card/OfficeNotesCard';
import PracticeStatusBadge from '../../../components/practice-status-badge/PracticeStatusBadge';
import ProviderInsuranceModal from '../../../components/provider-insurance-modal/ProviderInsuranceModal';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import RichTextCustomFieldCard from '../../../components/rich-text-card/RichTextCustomFieldCard';
import { useBatchedPractices } from '../../../hooks/useBatchedPractices';
import { usePermissions } from '../../../hooks/usePermissions';
import { useSupervisionRoleLabel } from '../../../hooks/useSupervisionRoleLabel';
import { getMarketList } from '../../../services/markets/getMarketList';
import { getPlansList } from '../../../services/plans/listPlans';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { getProviderById } from '../../../services/providers/getProviderById';
import { updateProviderById } from '../../../services/providers/updateProviderById';
import { analytics } from '../../../utils/analytics';
import { getPracticeClosures } from '../../../services/clousures/practiceClosureService';
import { PracticeClosure } from '../../../types/closures/types';
import {
  CustomFieldValue,
  OfficeHour,
  Practice,
  Provider,
  ProviderCustomFields,
} from '../../../types';
import { InsurancePlan } from '../../../types/plans/types';
import { CustomFields } from '../../../types/practices/types';
import {
  formatProviderName,
  getProviderCustomFieldLabel,
  getCustomFieldValue,
  getLabImagingDisplay,
  getLabImagingValue,
  normalizeCustomFieldValue,
} from '../../../utils/customFieldUtils';
import { getNewPatientPriorityProps } from '../../../utils/getNewPatientPriorityProps';
import { getProviderTypeFromData } from '../../../utils/getProviderTypeFromData';
import { isActiveProviderBridge } from '../../../utils/providerBridgeUtils';
import {
  constructPracticeAddress,
  mapPracticesForAbsencesCard,
  sortPracticesByPacStatus,
} from '../../../utils/practiceUtils';
import {
  isExternalPractice,
  isExternalProvider,
} from '../../../utils/externalEntity';
import ExternalAffordance from '../../../components/external-indicator/ExternalAffordance';
import {
  BOTH_FIELDS,
  PCP_ONLY_FIELDS,
  SPECIALIST_ONLY_FIELDS,
} from '../../../utils/providerCustomFields';
import {
  applySchedulingGroupOrder,
  buildServices,
} from '../../../utils/providerFieldMapping';
import { stateOptions } from '../../../utils/stateOptions';
import { sanitizeHtml } from '../../../utils/textConversion';
import PlanCardComponent from '../plans/PlanCompactCard';
import AthenaDepartmentLink from '../practices/AthenaDepartmentLink';
import PracticeClosuresCard from '../practices/PracticeClosuresCard';
import PracticeStatusBox from '../practices/PracticeStatusBox';
import CrossCoverageAllApptsBadge from './CrossCoverageAllApptsBadge';
import ClosestUrgentCareBadge from './ClosestUrgentCareBadge';
import CrossCoverageSickBadge from './CrossCoverageSickBadge';
import { COVERAGE_LABELS } from './coverageTypes';
import ExtractMedicareWellness from './extractMedicareWellness';
import ProviderAbsencesCard from './ProviderAbsencesCard';
import ProviderPacStatus from './ProviderPacStatus';

type Service = {
  name: string;
  detail: string;
  category: string;
  isRichText?: boolean;
};

const ProviderDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { accessToken, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loadingProvider, setLoadingProvider] = useState(true);
  const [loadingPractice, setLoadingPractice] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [services, setServices] = useState<Service[] | []>([]);
  const [error, setError] = useState<string | null>('');
  const [provider, setProviders] = useState<Provider | undefined>(
    undefined,
  );
  const [duplicateProviders, setDuplicateProviders] = useState<
    Record<string, string>
  >({});

  const [practices, setPractices] = useState<Practice[] | undefined>(
    undefined,
  );
  const [plans, setPlans] = useState<InsurancePlan[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [address, setAddress] = useState<
    { address: string; googleMapsUrl: string }[]
  >([]);
  const [closuresByPractice, setClosuresByPractice] = useState<
    Record<string, PracticeClosure[]>
  >({});
  const [closuresLoading, setClosuresLoading] = useState(false);

  const newPatientProps = getNewPatientPriorityProps(
    getCustomFieldValue(provider?.customFields, 'New-Patient'),
  );
  const supervisionRoleLabel = useSupervisionRoleLabel(provider);
  const getServiceLabel = (name: string) =>
    getProviderCustomFieldLabel(name, {
      'Patients-Virtual-Waiting-Schedule':
        'Patients to place on Virtual Waiting schedule',
      'Supervision-Physician': supervisionRoleLabel,
      'New-Telehealth': 'New Telehealth',
      Telehealth: 'Telehealth',
      'Takes-Walk-Ins': 'Takes Walk-ins',
    });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [officeHoursByPractice, setOfficeHoursByPractice] = useState<
    Record<string, OfficeHour[]>
  >({});
  const [athenaDepartments, setAthenaDepartments] = useState<
    {
      departmentName?: string;
      departmentLink: string | undefined;
      practiceName?: string;
      practiceLocation?: string;
      practiceId?: string;
    }[]
  >([]);
  const [isTerminateModalOpen, setIsTerminateModalOpen] =
    useState(false);
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] =
    useState(false);
  const [isAddPlanModalOpen, setIsAddPlanModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [terminationDate, setTerminationDate] = useState('');
  const [displayDate, setDisplayDate] = useState('');
  const [selectedPracticeId, setSelectedPracticeId] = useState('');
  const [selectedPracticeIds, setSelectedPracticeIds] = useState<
    string[]
  >([]);
  const providerExternalTablespace = (
    provider as
      | (Provider & { externalTablespace?: string | null })
      | undefined
  )?.externalTablespace;
  const providerIsExternal = isExternalProvider(provider);

  const [isLoading, setIsLoading] = useState(false); // New loading state for termination

  // Add state to force re-render of ProviderPacStatus
  const [pacStatusKey, setPacStatusKey] = useState(0);

  // Force re-render of ProviderPacStatus when provider data changes
  // Use stable stringified values to detect actual data changes, not just reference changes
  const providerBridgeKey = useMemo(
    () =>
      provider?.providerBridge
        ?.map(
          (b) =>
            `${b.practiceId}-${b.endDate || ''}-${
              b.displayDate || ''
            }`,
        )
        .join('|') || '',
    [provider?.providerBridge],
  );
  const relatedPracticesKey = useMemo(
    () =>
      provider?.relatedPractices
        ?.map((p) => p.id)
        .sort()
        .join(',') || '',
    [provider?.relatedPractices],
  );

  useEffect(() => {
    if (provider) {
      setPacStatusKey((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id, providerBridgeKey, relatedPracticesKey]); // Using specific keys instead of full provider object to prevent unnecessary updates

  // Collect all active practice IDs from the provider for batch fetching
  const activePracticeIds = useMemo(() => {
    if (!provider?.relatedPractices?.length && !provider?.providerBridge?.length) return [];
    const bridges = provider.providerBridge || [];
    const stubs = provider.relatedPractices?.length
      ? provider.relatedPractices.filter((p) => {
          const bridge = bridges.find((b) => b.practiceId === p.id);
          return !bridge || isActiveProviderBridge(bridge);
        })
      : bridges.filter(isActiveProviderBridge).map((b) => ({ id: b.practiceId } as Practice));
    return stubs.map((p) => p.id);
  }, [provider]);

  const { practicesMap: enrichedPracticesMap } = useBatchedPractices(activePracticeIds);

  // Get user permissions
  const {
    canEditProviders,
    canEditInsurance,
    isAdmin,
    isReady: permissionsReady,
  } = usePermissions();

  const formatPracticeNameWithTablespace = useCallback(
    (relatedPractice: Practice) => {
      const externalTablespace = (
        relatedPractice as Practice & {
          externalTablespace?: string | null;
        }
      ).externalTablespace;
      return externalTablespace
        ? `${relatedPractice.name} (Tablespace: ${externalTablespace})`
        : relatedPractice.name;
    },
    [],
  );

  // Memoize services to prevent unnecessary rebuilds when provider object reference changes
  // Only rebuild when customFields actually change
  const servicesMemo = useMemo(() => {
    if (provider?.customFields) {
      return buildServices(
        provider.customFields as ProviderCustomFields,
      );
    }
    return [];
  }, [provider?.customFields]);

  useEffect(() => {
    setServices(servicesMemo);
  }, [servicesMemo]); // servicesMemo already depends on provider?.customFields

  // Memoize practices array to prevent unnecessary re-renders in ProviderAbsencesCard
  const memoizedPractices = useMemo(
    () =>
      mapPracticesForAbsencesCard(
        provider?.relatedPractices ?? undefined,
      ),
    [provider?.relatedPractices],
  );

  // Track fetch state to prevent duplicate calls
  const lastFetchTimeRef = React.useRef<number>(0);
  const hasFetchedProviderRef = React.useRef<boolean>(false);
  const MIN_REFRESH_INTERVAL = 2000; // Minimum 2 seconds between refreshes

  // Reset fetch tracking when id changes
  useEffect(() => {
    hasFetchedProviderRef.current = false;
    lastFetchTimeRef.current = 0;
    setLoadingPractice(true);
    setPractices(undefined);
  }, [id]);

  const fetchProvider = useCallback(async () => {
    if (!id) return;

    // Prevent duplicate calls within minimum interval
    const now = Date.now();
    if (
      hasFetchedProviderRef.current &&
      now - lastFetchTimeRef.current < MIN_REFRESH_INTERVAL
    ) {
      return;
    }

    if (!isAuthenticated || !accessToken) {
      return;
    }

    lastFetchTimeRef.current = now;
    hasFetchedProviderRef.current = true;

    setLoadingProvider(true);
    try {
      const { data } = await getProviderById(id);
      setProviders(data?.provider);

      // Get duplicateProviders from provider detail API response
      if (data?.duplicateProviders) {
        setDuplicateProviders(data.duplicateProviders);
      } else {
        setDuplicateProviders({});
      }
      analytics.trackEvent('view', 'provider');
    } catch (error) {
      setError('Failed to load provider details. Please try again.');
      setLoadingPractice(false);
    } finally {
      setLoadingProvider(false);
    }
  }, [id, accessToken, isAuthenticated]);

  useEffect(() => {
    if (!id) return;

    // Only fetch if we haven't fetched yet for this id
    if (hasFetchedProviderRef.current) return;

    if (isAuthenticated && accessToken) {
      fetchProvider();
    }
  }, [id, fetchProvider, accessToken, isAuthenticated]);

  // Scroll to top when component mounts or route changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  // Handle automatic opening of insurance modal from query parameter
  useEffect(() => {
    const openInsuranceModal = searchParams.get('openInsuranceModal');
    const from = searchParams.get('from');

    if (
      openInsuranceModal === 'true' &&
      from === 'resource-grid' &&
      provider &&
      !isInsuranceModalOpen
    ) {
      setIsInsuranceModalOpen(true);

      // Show toast with context about the source
      const planId = searchParams.get('planId');

      toast({
        title: 'Editing Insurance Plans',
        description: planId
          ? 'You can edit insurance plans for this provider. Changes will be reflected in the plan view.'
          : 'You can edit insurance plans for this provider.',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });

      // Clean up the URL parameters
      setSearchParams((prev: URLSearchParams) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('openInsuranceModal');
        newParams.delete('from');
        newParams.delete('planId');
        newParams.delete('marketId');
        return newParams;
      });
    }
  }, [
    searchParams,
    provider,
    isInsuranceModalOpen,
    setSearchParams,
    toast,
  ]);

  // Track fetch state for plans to prevent duplicate calls
  const hasFetchedPlansRef = React.useRef<boolean>(false);
  const lastFetchPlansTimeRef = React.useRef<number>(0);

  // Reset plans fetch tracking when id changes
  useEffect(() => {
    hasFetchedPlansRef.current = false;
    lastFetchPlansTimeRef.current = 0;
  }, [id]);

  const fetchPlans = useCallback(async () => {
    if (!id) return;

    // Prevent duplicate calls within minimum interval
    const now = Date.now();
    if (
      hasFetchedPlansRef.current &&
      now - lastFetchPlansTimeRef.current < MIN_REFRESH_INTERVAL
    ) {
      return;
    }

    if (!isAuthenticated || !accessToken) {
      return;
    }

    lastFetchPlansTimeRef.current = now;
    hasFetchedPlansRef.current = true;

    try {
      setLoadingPlans(true);
      const { data } = await getPlansList(
        null,
        {
          providerIds: id,
        },
        10,
      );
      // Use functional update to ensure we get the latest state
      setPlans((prevPlans) => {
        const newPlans = data?.insurancePlans || [];
        // Only update if the data has actually changed
        if (JSON.stringify(prevPlans) !== JSON.stringify(newPlans)) {
          return newPlans;
        }
        return prevPlans;
      });
    } catch (err) {
      setError('Failed to load plans. Please try again.');
    } finally {
      setLoadingPlans(false);
    }
  }, [id, accessToken, isAuthenticated]);

  useEffect(() => {
    if (!id) return;

    // Only fetch if we haven't fetched yet for this id
    if (hasFetchedPlansRef.current) return;

    if (isAuthenticated && accessToken) {
      fetchPlans();
    }
  }, [id, fetchPlans, accessToken, isAuthenticated]);

  // useEffect(() => {
  //   if (plans.length > 0) {
  //     setColumns(plansColumns(plans))
  //   }
  //   if (!plansLoading) {
  //     setHasFetched(true)
  //   }
  // }, [plans, plansLoading])

  useEffect(() => {
    if (provider?.relatedPractices?.length) {
      const practiceIds = provider.relatedPractices.map(
        (practice) => practice.id,
      );

      // Fetch office hours for each practice separately to prevent data mixing
      Promise.all(
        practiceIds.map((practiceId) =>
          postOfficeHoursList([practiceId]).then((data) => ({
            practiceId,
            officeHours: data?.data.office_hours || [],
          })),
        ),
      )
        .then((results) => {
          // Store office hours by practice ID
          const officeHoursByPractice: Record<string, OfficeHour[]> =
            {};
          results.forEach(({ practiceId, officeHours }) => {
            officeHoursByPractice[practiceId] = officeHours;
          });
          setOfficeHoursByPractice(officeHoursByPractice);
        })
        .catch(() => {
          setOfficeHoursByPractice({});
        });
    } else {
      setOfficeHoursByPractice({});
    }
  }, [id, provider?.relatedPractices]);

  // OPTIMIZATION: Use provider.relatedPractices directly instead of making redundant API calls
  // The getProviderById API already returns complete practice data in relatedPractices array
  const fetchPractices = useCallback(
    (providerToUse?: Provider) => {
      // Use provided provider or fall back to state provider
      const providerData = providerToUse || provider;

      const enrichPracticeFromMap = (practice: Practice): Practice => {
        const fetched = enrichedPracticesMap[practice.id];
        if (fetched) {
          return {
            ...practice,
            ...fetched,
            customFields: fetched.customFields || practice.customFields || {},
          };
        }
        const practiceWithPossibleFields = practice as Practice & {
          custom_fields?: CustomFields;
        };
        return {
          ...practice,
          customFields:
            (practiceWithPossibleFields.customFields as CustomFields) ||
            practiceWithPossibleFields.custom_fields ||
            practice.customFields ||
            {},
        };
      };

      if (providerData?.relatedPractices?.length || providerData?.providerBridge?.length) {
        setError((prev) =>
          prev === 'Failed to load provider details. Please try again.' ? null : prev,
        );

        // Only show practices where the provider's bridge is still active.
        // A bridge is inactive when endDate is today or earlier and
        // displayDate (grace-period) is also absent or in the past.
        const bridges = providerData.providerBridge || [];

        // When relatedPractices is absent but providerBridge has entries, fall
        // back to stubs keyed by practiceId; enrichPracticeFromMap will
        // merge in data from the batched hook if available.
        const allPractices = providerData.relatedPractices?.length
          ? providerData.relatedPractices
              .filter((practice) => {
                const practiceBridges = bridges.filter(
                  (b) => b.practiceId === practice.id,
                );
                return (
                  !practiceBridges.length ||
                  practiceBridges.some(isActiveProviderBridge)
                );
              })
              .map((practice) => {
                const practiceWithCustomFields = practice as Practice & {
                  custom_fields?: Record<string, unknown>;
                };
                return {
                  ...practice,
                  customFields:
                    practiceWithCustomFields.custom_fields ||
                    practice.customFields ||
                    {},
                } as Practice;
              })
          : Array.from(
              new Map(
                bridges
                  .filter((b) => isActiveProviderBridge(b))
                  .map((b) => [b.practiceId, b.practiceId]),
              ).values(),
            ).map((practiceId) => ({ id: practiceId } as Practice));

        const enrichedPractices = allPractices.map((practice) =>
          enrichPracticeFromMap(practice),
        );

        // On-PAC practices first so agents don't see "Not on PAC" at the top
        setPractices(sortPracticesByPacStatus(enrichedPractices));
        setLoadingPractice(false);
      } else {
        setError((prev) =>
          prev === 'Failed to load provider details. Please try again.' ? null : prev,
        );
        setPractices([]);
        setLoadingPractice(false);
      }
    },
    [provider, enrichedPracticesMap],
  );

  // Run fetchPractices when provider loads (initial stub render) and again
  // when the batched practices map is populated with enriched data.
  useEffect(() => {
    if (provider) {
      fetchPractices();
    }
  }, [provider, fetchPractices]);

  // Stable string key derived from practice IDs — changes only when the SET of IDs changes,
  // not when the array reference changes (which happens on every fetchPractices call).
  const practiceIdsKey = useMemo(
    () => (practices ?? []).map((p) => p.id).sort().join(','),
    [practices],
  );

  // Batch-fetch all closure data once practices are known.
  // Depends on practiceIdsKey (not practices) to avoid double-fetch when fetchPractices
  // sets a new array reference with the same IDs.
  useEffect(() => {
    if (!practiceIdsKey) return;
    const ids = practiceIdsKey.split(',').filter(Boolean);
    if (!ids.length) return;
    setClosuresLoading(true);
    getPracticeClosures({ practiceIds: ids })
      .then((closures) => {
        const grouped: Record<string, PracticeClosure[]> = {};
        closures.forEach((c) => {
          if (c.practiceId) {
            grouped[c.practiceId] = grouped[c.practiceId] || [];
            grouped[c.practiceId].push(c);
          }
        });
        setClosuresByPractice(grouped);
      })
      .catch((error) => {
        console.warn('Failed to fetch closures for provider practices:', error);
      })
      .finally(() => setClosuresLoading(false));
  }, [practiceIdsKey]);

  useEffect(() => {
    if (practices?.length) {
      const completeAddresses = practices.map((practice) => {
        const address = constructPracticeAddress(practice);
        const encodedAddress = encodeURIComponent(address);
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        return { address, googleMapsUrl };
      });
      setAddress(completeAddresses);
    }
  }, [practices]);

  useEffect(() => {
    if (practices?.length && provider?.providerBridge?.length) {
      const departmentLinks = practices
        .map((practice) => {
          // Find the provider bridge for this practice
          const bridge = provider.providerBridge?.find(
            (bridge) => bridge.practiceId === practice.id,
          );

          // Only include practices that have a provider bridge
          if (!bridge) {
            return null;
          }

          const departmentName =
            bridge?.metadata?.['Athena-Department'];
          const departmentLink = bridge?.metadata?.['Athena-Link'];

          // Build location string (city, state)
          const location = practice
            ? [practice.city, practice.state]
                .filter(Boolean)
                .join(', ')
            : undefined;

          // Include the entry even if department info is missing
          // This ensures we show the practice name regardless
          return {
            departmentName:
              departmentName &&
              departmentName.trim() !== '' &&
              departmentName !== '-'
                ? departmentName.trim()
                : undefined, // Allow undefined for missing department
            departmentLink:
              departmentLink &&
              departmentLink.trim() !== '' &&
              departmentLink !== '-' &&
              departmentLink !== 'null' &&
              departmentLink !== 'undefined'
                ? departmentLink.trim()
                : undefined,
            practiceName: practice?.name,
            practiceLocation: location,
            practiceId: practice?.id,
          };
        })
        .filter(
          (item): item is NonNullable<typeof item> => item !== null,
        );

      setAthenaDepartments(departmentLinks);
    } else {
      // Clear departments if no practices or provider bridge
      setAthenaDepartments([]);
    }
  }, [practices, provider?.providerBridge]);

  // Canonical Provider-Type used locally (prefer CF, fallback to specialty list)
  const providerTypeCanon = getProviderTypeFromData(
    provider?.customFields as ProviderCustomFields | undefined,
    provider?.specialty,
  );

  const isPCP = providerTypeCanon === 'Pcp';

  // Get provider state for AddInsurancePlansModal
  const providerStateForModal = useMemo(() => {
    if (practices && practices.length > 0 && practices[0]?.state) {
      const practiceState = practices[0].state;
      // Convert state name to abbreviation if needed
      if (practiceState.length === 2) {
        return practiceState.toUpperCase();
      } else {
        const stateOption = stateOptions.find(
          (option: { label: string; value: string }) =>
            option.label.toLowerCase() ===
            practiceState.toLowerCase(),
        );
        return stateOption ? stateOption.value : practiceState;
      }
    }
    return undefined;
  }, [practices]);

  // Get existing insurance IDs for AddInsurancePlansModal
  const existingInsuranceIds = useMemo(() => {
    const ids = new Set<string>();
    plans.forEach((insurancePlan) => {
      insurancePlan.plans?.forEach((plan) => {
        if (plan?.insurance?.id) {
          ids.add(plan.insurance.id);
        }
      });
    });
    return ids;
  }, [plans]);

  const cernerPoolEntries = useMemo(() => {
    const cernerPools = provider?.customFields?.['Cerner-pool'] as
      | CustomFieldValue[]
      | undefined;
    if (!cernerPools || cernerPools.length === 0) return [];

    const normalizedValues = cernerPools
      .map((pool) => {
        const value = normalizeCustomFieldValue(
          String(pool?.value ?? ''),
        );
        if (!value) return null;
        const metadata = (pool?.metadata || {}) as {
          relatedPracticeId?: string | string[];
          relatedPracticeName?: string;
        };
        const relatedPracticeId =
          typeof metadata.relatedPracticeId === 'string'
            ? metadata.relatedPracticeId
            : undefined;
        const relatedPracticeName =
          typeof metadata.relatedPracticeName === 'string'
            ? metadata.relatedPracticeName
            : undefined;
        return {
          value,
          relatedPracticeId,
          relatedPracticeName,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> =>
        Boolean(entry),
      );

    if (normalizedValues.length === 0) return [];

    const remaining = [...normalizedValues];
    const orderSource =
      athenaDepartments.length > 0
        ? athenaDepartments.map((dept) => ({
            practiceId: dept.practiceId,
            practiceName: dept.practiceName,
            practiceLocation: dept.practiceLocation,
          }))
        : (practices || []).map((practice) => ({
            practiceId: practice.id,
            practiceName: practice.name,
            practiceLocation: [practice.city, practice.state]
              .filter(Boolean)
              .join(', '),
          }));

    const orderedEntries: Array<{
      practiceName?: string;
      practiceLocation?: string;
      value: string;
    }> = [];

    orderSource.forEach((practice) => {
      const matches = remaining.filter(
        (entry) =>
          (practice.practiceId &&
            entry.relatedPracticeId === practice.practiceId) ||
          (practice.practiceName &&
            entry.relatedPracticeName === practice.practiceName),
      );
      if (matches.length > 0) {
        orderedEntries.push({
          practiceName: practice.practiceName,
          practiceLocation: practice.practiceLocation,
          value: matches.map((match) => match.value).join(', '),
        });
        matches.forEach((match) => {
          const index = remaining.indexOf(match);
          if (index >= 0) remaining.splice(index, 1);
        });
      }
    });

    remaining.forEach((entry) => {
      orderedEntries.push({
        practiceName: entry.relatedPracticeName,
        value: entry.value,
      });
    });

    return orderedEntries;
  }, [provider?.customFields, athenaDepartments, practices]);

  // Build the allow-list for this provider type: all BOTH fields + the type-specific exclusives
  const allowedForProvider = new Set(
    isPCP
      ? [...BOTH_FIELDS, ...PCP_ONLY_FIELDS]
      : [...BOTH_FIELDS, ...SPECIALIST_ONLY_FIELDS],
  );

  // Treatments column: show only fields allowed for this provider type
  const treatmentFieldsToShow = services.filter(
    (service) =>
      service.category === 'Treatments' &&
      allowedForProvider.has(service.name),
  );

  // Scheduling column: show only fields allowed for this provider type
  let schedulingFieldsToShow = services.filter(
    (service) =>
      service.category === 'Scheduling' &&
      allowedForProvider.has(service.name) &&
      service.name !== 'Scheduling-Notes',
  );

  schedulingFieldsToShow = applySchedulingGroupOrder(
    schedulingFieldsToShow,
    isPCP,
  );

  // Filter out legacy "Telehealth" field; show New-Telehealth and Out-Of-State for all provider types
  schedulingFieldsToShow = schedulingFieldsToShow.filter(
    (s) => s.name !== 'Telehealth',
  );

  // Omit Cross-Coverage-for-Sick-Market row when the field has no content
  schedulingFieldsToShow = schedulingFieldsToShow.filter((s) => {
    if (s.name !== 'Cross-Coverage-for-Sick-Market' && s.name !== 'Cross-Coverage-Sick') return true;
    const cf = provider?.customFields[s.name] as CustomFieldValue[] | undefined;
    if (!Array.isArray(cf) || cf.length === 0) return false;
    return cf.some((r) => {
      const val = typeof r.value === 'string' ? r.value.trim() : '';
      const providers = r.metadata?.providers as unknown[] | undefined;
      const coverageType = r.metadata?.coverageType as string | undefined;
      return (Boolean(val) && val !== 'None') || Boolean(coverageType && COVERAGE_LABELS[coverageType]) || (Array.isArray(providers) && providers.length > 0);
    });
  });

  // Group Telehealth fields together (New-Telehealth + Out-Of-State for all providers)
  const telehealthGroupOrder = [
    'New-Telehealth',
    'Out-Of-State-Telehealth',
  ];

  const telehealthItems = telehealthGroupOrder
    .map((name) =>
      schedulingFieldsToShow.find((s) => s.name === name),
    )
    .filter((s): s is Service => Boolean(s));
  if (telehealthItems.length > 0) {
    const isTele = (n: string) => telehealthGroupOrder.includes(n);
    const reordered: Service[] = [];
    let inserted = false;
    for (const s of schedulingFieldsToShow) {
      if (!inserted && isTele(s.name)) {
        reordered.push(...telehealthItems);
        inserted = true;
      }
      if (!isTele(s.name)) {
        reordered.push(s);
      }
    }
    schedulingFieldsToShow = reordered;
  }

  if (error) {
    return (
      <NoItemsFound
        mainMessage="Failed to load provider details"
        detailMessage={error}
        refetch={fetchProvider}
      />
    );
  }

  const handleTerminateProvider = async () => {
    // Use selectedPracticeIds if available (multi-select), otherwise fall back to selectedPracticeId (single select)
    const practicesToTerminate =
      selectedPracticeIds.length > 0
        ? selectedPracticeIds
        : selectedPracticeId
          ? [selectedPracticeId]
          : [];

    if (practicesToTerminate.length === 0 || !terminationDate) return;

    try {
      setIsLoading(true); // Start loading

      // Update providerBridge for all selected practices
      const updatedProviderBridge = provider?.providerBridge?.map(
        (item) =>
          practicesToTerminate.includes(item.practiceId)
            ? {
                ...item,
                endDate: terminationDate,
                ...(displayDate && { displayDate }),
              }
            : item,
      );

      await updateProviderById(provider?.id, {
        id: provider?.id,
        providerBridge: updatedProviderBridge,
      });

      // Update local state immediately instead of refetching
      if (provider && updatedProviderBridge) {
        const updatedProvider = {
          ...provider,
          providerBridge: updatedProviderBridge,
        };
        setProviders(updatedProvider);

        // Immediately update practices list with the updated provider
        // This ensures practices are filtered based on new termination status
        // and badges are updated in all display areas
        fetchPractices(updatedProvider);

        // Force ProviderPacStatus to re-render with updated data
        setPacStatusKey((prev) => prev + 1);
      }

      const practiceCount = practicesToTerminate.length;
      toast({
        title: 'Provider terminated successfully.',
        description:
          practiceCount > 1
            ? `Provider terminated at ${practiceCount} practices.`
            : 'Provider terminated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Dispatch event to notify other pages (like PracticeDetail) to refresh
      practicesToTerminate.forEach((practiceId) => {
        window.dispatchEvent(
          new CustomEvent('providerTerminated', {
            detail: { providerId: provider?.id, practiceId },
          }),
        );
      });

      setIsTerminateModalOpen(false);
      setIsConfirmModalOpen(false);
      setSelectedPracticeIds([]); // Reset multi-select
      setSelectedPracticeId(''); // Reset single select
      setTerminationDate(''); // Reset termination date
      setDisplayDate(''); // Reset display date
    } catch (error) {
      toast({
        title: 'Failed to terminate provider.',
        description:
          typeof error === 'object' &&
          error !== null &&
          'message' in error
            ? String((error as { message?: string }).message)
            : 'An error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const isProviderTerminated = () => {
    if (
      !provider?.providerBridge ||
      provider.providerBridge.length === 0
    ) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if ALL practices are terminated (not just some)
    // Only hide terminate option when provider is terminated at ALL practices
    return provider.providerBridge.every((bridge) => {
      if (!bridge.endDate) return false; // No end date = not terminated

      const endDate = new Date(bridge.endDate);
      endDate.setHours(0, 0, 0, 0);

      // If endDate is past, check displayDate
      if (endDate <= today) {
        // If displayDate exists and is in the future, provider is not terminated yet
        if (bridge.displayDate) {
          const displayDate = new Date(bridge.displayDate);
          displayDate.setHours(0, 0, 0, 0);
          return displayDate < today; // Only terminated if displayDate is also past
        }
        return true; // Past end date and no displayDate = terminated
      }

      return false; // Future end date = not terminated
    });
  };

  // Helper function to handle market navigation with fallback
  const handleMarketNavigation = async (practice: Practice) => {
    // Check for marketId (camelCase) first, then marketid (lowercase) as fallback
    const marketId =
      practice?.marketId ||
      (practice as unknown as { marketid?: string })?.marketid;

    if (marketId) {
      navigate(`/markets/${marketId}`);
      return;
    }

    // If marketId is not available, try to find it by market name
    if (practice?.market) {
      try {
        const { data } = await getMarketList(undefined, [
          practice.market,
        ], undefined, isAdmin());
        if (data?.states && data.states.length > 0) {
          // Search through all states and their markets
          for (const state of data.states) {
            const market = state.markets.find(
              (m: { name: string; id: string }) =>
                m.name === practice.market,
            );
            if (market) {
              navigate(`/markets/${market.id}`);
              return;
            }
          }
        }
      } catch {
        // Ignore errors
      }
    }

    // If we can't find the market ID, show a warning
    toast({
      title: 'Market Link Unavailable',
      description:
        'Market ID is not available for this practice. Please contact support.',
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
  };

  if (loadingProvider || !isAuthenticated || !permissionsReady) {
    return <PageLoader />;
  }


  return (
    <>
      <Helmet>
        <title>
          {provider
            ? `${formatProviderName({ ...provider, customFields: provider.customFields })} | Provider | Resource Matrix — Denim Health`
            : 'Provider | Resource Matrix — Denim Health'}
        </title>
      </Helmet>
      <Flex
        justifyContent={'space-between'}
        mt="2rem"
        ml={{ base: '1rem', md: '2rem' }}
        mr={{ base: '1rem', md: '2rem' }}
        mb="1.62rem"
      >
        <Breadcrumb separator={<ChevronRightIcon color="gray.500" />}>
          <BreadcrumbItem
            onClick={() => {
              navigate('/');
            }}
          >
            <BreadcrumbLink>
              <Text
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                color="gray.700"
              >
                Home
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem
            onClick={() => {
              navigate('/providers');
            }}
          >
            <BreadcrumbLink>
              <Text
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1.25rem"
                color="gray.700"
              >
                Providers
              </Text>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbItem isCurrentPage>
            <Skeleton isLoaded={!loadingProvider && !loadingPractice}>
              <BreadcrumbLink href="#">
                <Text
                  fontSize="0.875rem"
                  fontWeight="400"
                  lineHeight="1.25rem"
                  color="gray.700"
                >
                  {provider
                    ? formatProviderName({
                        ...provider,
                        customFields: provider.customFields,
                      })
                    : 'Provider'}
                </Text>
              </BreadcrumbLink>
            </Skeleton>
          </BreadcrumbItem>
        </Breadcrumb>
        {(canEditProviders() || isAdmin()) && (
          <Menu isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
            <MenuButton
              as={IconButton}
              icon={<img src={MoreIcon} />}
              size="sm"
              aria-label="Options"
              borderRadius="0.375rem"
              background={'background.secondary'}
              width="2.5rem"
              height="2.5rem"
              cursor={'pointer'}
              border={isOpen ? '2px solid border' : 'none'}
              borderColor={isOpen ? 'border.subtle' : 'none'}
              transition="all 0.2s ease-in-out"
            ></MenuButton>
            <MenuList>
              {(canEditProviders() || isAdmin()) && (
                <MenuItem
                  isDisabled={!id}
                  onClick={() => navigate(`/providers/edit/${id}`)}
                >
                  Edit Provider
                </MenuItem>
              )}
              {(canEditProviders() || isAdmin()) &&
                !isProviderTerminated() && (
                  <MenuItem
                    onClick={() => {
                      setIsTerminateModalOpen(true);
                      setTerminationDate(
                        (() => {
                          const today = new Date();
                          return `${today.getFullYear()}-${String(
                            today.getMonth() + 1,
                          ).padStart(2, '0')}-${String(
                            today.getDate(),
                          ).padStart(2, '0')}`;
                        })(),
                      ); // Initialize with today's date
                      // Auto-select all practices for convenience
                      const allPracticeIds =
                        provider?.relatedPractices?.map(
                          (p) => p.id,
                        ) || [];
                      setSelectedPracticeIds(allPracticeIds);
                      setSelectedPracticeId(''); // Clear single select
                    }}
                  >
                    Terminate Provider
                  </MenuItem>
                )}
              <MenuItem isDisabled={true}>View Audit Log</MenuItem>
              <MenuItem
                onClick={() => {
                  onClose();
                }}
              >
                Close
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </Flex>

      {/* Non-blocking render: show page even while provider is loading */}
      <>
        <Stack gap="1rem" ml={{ base: '1rem', md: '2rem' }} mr={{ base: '1rem', md: '2rem' }} mb="3.63rem">
          <Skeleton isLoaded={!loadingProvider}>
            {' '}
            <Flex
              alignItems={'center'}
              mb={'2rem'}
              gap="0.75rem"
              flexWrap="wrap"
            >
              {provider ? (
                <>
                  <ProviderName
                    provider={provider}
                    duplicateProviders={duplicateProviders}
                    mode="chip"
                    size="xl"
                    truncate={false}
                  />
                  {providerExternalTablespace && (
                    <CustomFieldLabel
                      customField="Tablespace"
                      fieldValue={providerExternalTablespace}
                      fieldValueBgColor="background.gray100"
                    />
                  )}
                  {!providerIsExternal && (
                    <ProviderPacStatus
                      key={pacStatusKey} // Force re-render when provider data changes
                      providerBridge={provider?.providerBridge}
                      relatedPractices={provider?.relatedPractices}
                    />
                  )}
                </>
              ) : (
                <Text
                  fontSize="1.875rem"
                  fontWeight="700"
                  lineHeight={'133%'}
                  color="text.primary"
                >
                  Provider
                </Text>
              )}
            </Flex>
          </Skeleton>

          <Flex gap="1rem" alignItems={'flex-start'} flexWrap={{ base: 'wrap', lg: 'nowrap' }}>
            <Stack width={{ base: '100%', lg: '30%' }}>
              <Text
                fontSize={'1.25rem'}
                fontWeight={500}
                lineHeight={'120%'}
                color={'text.secondary'}
                mb="0.69rem"
              >
                Practice details
              </Text>
              {loadingPractice ? (
                <Stack gap="0.75rem">
                  <Skeleton height="14rem" borderRadius="0.5rem" />
                  <Skeleton height="14rem" borderRadius="0.5rem" />
                </Stack>
              ) : (
                <>
                  {practices?.length ? (
                    practices.map((practice, index) => (
                      <VStack
                        key={practice.id}
                        borderRadius={'0.5rem'}
                        border="2px solid"
                        borderColor="background.brandLight"
                        align="left"
                        p="1rem"
                        mb={
                          index < practices.length - 1 ? '1rem' : '0'
                        } // Add spacing between multiple practices
                      >
                        <Text
                          color={'gray.600'}
                          fontSize={'0.875rem'}
                          fontWeight={'700'}
                          lineHeight={'1.05rem'}
                          onClick={() =>
                            handleMarketNavigation(practice)
                          }
                          _hover={{
                            cursor:
                              practice?.marketId ||
                              (
                                practice as unknown as {
                                  marketid?: string;
                                }
                              )?.marketid
                                ? 'pointer'
                                : 'not-allowed',
                            color:
                              practice?.marketId ||
                              (
                                practice as unknown as {
                                  marketid?: string;
                                }
                              )?.marketid
                                ? 'blue.500'
                                : 'gray.400',
                          }}
                          cursor={
                            practice?.marketId ||
                            (
                              practice as unknown as {
                                marketid?: string;
                              }
                            )?.marketid
                              ? 'pointer'
                              : 'not-allowed'
                          }
                          opacity={
                            practice?.marketId ||
                            (
                              practice as unknown as {
                                marketid?: string;
                              }
                            )?.marketid
                              ? 1
                              : 0.6
                          }
                        >
                          {practice?.market || 'Unknown Market'}
                        </Text>
                        <Flex
                          gap="0.5rem"
                          alignItems="center"
                          mb="0.75rem"
                        >
                          <Text
                            fontSize={'1.5rem'}
                            fontWeight={'700'}
                            lineHeight={'1.995rem'}
                            color={'text.primary'}
                            onClick={() =>
                              navigate(`/practices/${practice.id}`)
                            }
                            _hover={{
                              color: 'text.brandLight',
                            }}
                            cursor="pointer"
                          >
                            {formatPracticeNameWithTablespace(
                              practice,
                            )}
                          </Text>
                          {isExternalPractice(practice) && (
                            <ExternalAffordance
                              entityType="practice"
                              size="sm"
                            />
                          )}
                          {/* Practice Status Badge */}
                          {practice && !isExternalPractice(practice) && (
                            <PracticeStatusBadge
                              active={practice.active ?? true}
                              practiceOnPac={practice.practiceOnPac}
                              practiceHospitalFollowupPac={practice.practiceHospitalFollowupPac}
                              practiceSickCrossCoveragePac={practice.practiceSickCrossCoveragePac}
                            />
                          )}
                        </Flex>
                        <VStack
                          gap="0.5rem"
                          align="start"
                          flex={1}
                          minWidth={0}
                        >
                          <CustomFieldBadge
                            customField="Backline"
                            fieldValue={
                              getCustomFieldValue(
                                practice?.customFields,
                                'Backline',
                              ) || '-'
                            }
                            bgColor="#FED7D7"
                            titleColor="#9B2C2C"
                            valueColor="#9B2C2C"
                          />
                          <Flex
                            gap="0.62rem"
                            flexWrap="wrap"
                            alignItems="center"
                          >
                            <CustomFieldBadge
                              customField="Escalation"
                              fieldValue={
                                getCustomFieldValue(
                                  practice?.customFields,
                                  'Manager-Line',
                                ) || '-'
                              }
                              bgColor="background.orange200"
                            />
                            <Text
                              fontSize="0.75rem"
                              fontWeight="700"
                              lineHeight="1rem"
                              color="text.primary"
                            >
                              {getCustomFieldValue(
                                practice?.customFields,
                                'Manager-Name',
                              ) || '-'}
                            </Text>
                            <Text
                              fontSize="0.75rem"
                              fontWeight="400"
                              lineHeight="1rem"
                              color="text.orange600"
                              wordBreak="break-word"
                            >
                              {getCustomFieldValue(
                                practice?.customFields,
                                'Manager-Email',
                              ) || '-'}
                            </Text>
                          </Flex>
                          <CustomFieldBadge
                            customField="Answering Service"
                            fieldValue={
                              getCustomFieldValue(
                                practice?.customFields,
                                'After-Hours',
                              ) || '-'
                            }
                            bgColor="background.cyan100"
                          />
                          <CustomFieldBadge
                            customField="Phone"
                            fieldValue={`${
                              getCustomFieldValue(
                                practice?.customFields,
                                'Phone',
                              ) || '-'
                            }`}
                            bgColor="background.gray50"
                          />
                          <CustomFieldBadge
                            customField="Fax"
                            fieldValue={
                              getCustomFieldValue(
                                practice?.customFields,
                                'Fax',
                              ) || '-'
                            }
                            bgColor={'background.gray50'}
                          />
                          {getCustomFieldValue(
                            practice?.customFields,
                            'Group-TID',
                          ) && (
                            <CustomFieldBadge
                              customField="Tax ID"
                              fieldValue={getCustomFieldValue(
                                practice?.customFields,
                                'Group-TID',
                              )}
                              bgColor={'background.gray50'}
                            />
                          )}

                          <PracticeStatusBox
                            timezone={practice?.timezone || ''}
                            state={practice.state || ''}
                            unFormattedOfficeHours={
                              officeHoursByPractice[practice.id] || []
                            }
                            hideMoreDetails={false}
                            width="100%"
                            practiceId={practice.id}
                            defaultOpen
                            clinicClosures={closuresByPractice[practice.id] ?? null}
                            isClosuresLoading={closuresLoading}
                          />
                          <Flex
                            gap="0.5rem"
                            wrap="wrap"
                            alignItems="center"
                          >
                            {(
                              [
                                ['Labs Hours', 'Labs-Drawn-Hours'],
                                [
                                  'Imaging Hours',
                                  'Imaging-Available-Hours',
                                ],
                              ] as const
                            ).map(([label, fieldName]) => {
                              const { displayText, bgColor } =
                                getLabImagingDisplay(
                                  getLabImagingValue(
                                    practice?.customFields,
                                    fieldName,
                                  ),
                                );
                              return (
                                <CustomFieldLabel
                                  key={fieldName}
                                  customField={label}
                                  fieldValue={displayText}
                                  fieldValueBgColor={bgColor}
                                />
                              );
                            })}
                          </Flex>
                        </VStack>
                        <Flex
                          mt="0.75rem"
                          mb="0.5rem"
                          gap="0.5rem"
                          wrap="nowrap"
                          width="100%"
                          justify={'space-between'}
                        >
                          <Text
                            fontSize="0.75rem"
                            fontWeight="400"
                            lineHeight="1.125rem"
                            color="text.secondary"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {address[index]?.address}
                          </Text>
                          <DirectionsButton
                            googleMapsUrl={
                              address[index]?.googleMapsUrl || ''
                            }
                          />
                        </Flex>

                        {/* Practice Related Cards */}
                        {practices && practices.length > 0 && (
                          <VStack
                            spacing="0.5rem"
                            width="100%"
                            mt="1rem"
                          >
                            {/* Office Notes Card */}
                            <OfficeNotesCard
                              practice={practice}
                              practices={practices}
                              width="100%"
                            />

                            {/* Daily Alerts Card */}
                            <DailyAlertsCard
                              practice={practice}
                              practices={practices}
                              width="100%"
                            />

                            {/* Practice Closures Card */}
                            <PracticeClosuresCard
                              practiceId={practice.id}
                              practiceTimezone={practice.timezone ?? null}
                              practiceState={practice.state ?? null}
                              closures={closuresByPractice[practice.id] ?? null}
                            />
                          </VStack>
                        )}

                        {/* Show message if no practices available */}
                        {(!practices || practices.length === 0) && (
                          <Text
                            fontSize="0.75rem"
                            color="gray.500"
                            textAlign="center"
                            mt="1rem"
                          >
                            No practice-related information available
                          </Text>
                        )}
                      </VStack>
                    ))
                  ) : (
                    <Text fontSize="0.875rem" color="gray.500">
                      No related practices available
                    </Text>
                  )}
                </>
              )}
            </Stack>
            <Stack width={{ base: '100%', lg: '70%' }}>
              <Text
                fontSize={'1.25rem'}
                fontWeight={500}
                lineHeight={'120%'}
                color={'text.secondary'}
                mb="0.69rem"
              >
                Provider details
              </Text>
              <VStack align="left" gap="0.81rem">
                <Flex alignItems={'center'}>
                  {provider ? (
                    <ProviderName
                      provider={provider}
                      duplicateProviders={duplicateProviders}
                      mode="link"
                      size="xl"
                      truncate={false}
                    />
                  ) : (
                    <Text
                      color="text.primary"
                      fontSize="2rem"
                      fontWeight="700"
                      lineHeight={'133%'}
                    >
                      Provider
                    </Text>
                  )}
                  {providerExternalTablespace && (
                    <CustomFieldLabel
                      customField="Tablespace"
                      fieldValue={providerExternalTablespace}
                      fieldValueBgColor="background.gray100"
                    />
                  )}
                  <Tooltip label={'Athena ID'}>
                    <Flex ml="1.5rem" height={'1.25rem'} mr="1.5rem">
                      <CustomFieldLabel
                        customField="Athena ID"
                        fieldValue={
                          getCustomFieldValue(
                            provider?.customFields,
                            'Athena-ID',
                          ) || 'Not specified'
                        }
                        fieldValueBgColor={
                          getCustomFieldValue(
                            provider?.customFields,
                            'Gender',
                          ) === 'Male'
                            ? 'blue.100'
                            : getCustomFieldValue(
                                  provider?.customFields,
                                  'Gender',
                                ) === 'Female'
                              ? 'background.pink100'
                              : 'gray.100'
                        }
                        fieldValueTextColor={
                          getCustomFieldValue(
                            provider?.customFields,
                            'Gender',
                          ) === 'Male'
                            ? 'blue.800'
                            : getCustomFieldValue(
                                  provider?.customFields,
                                  'Gender',
                                ) === 'Female'
                              ? 'text.pink800'
                              : 'gray.800'
                        }
                      />
                    </Flex>
                  </Tooltip>
                </Flex>
                <Flex align="left" wrap={'wrap'}>
                  <Flex mt="0.75rem" gap="0.75rem" wrap="wrap">
                    {provider?.pronunciation && (
                      <CustomFieldLabel
                        customField="Pronunciation"
                        fieldValue={`${provider?.pronunciation}`}
                        fieldValueBgColor="background.teal100"
                        fieldValueTextColor="text.primary"
                      />
                    )}
                    {provider?.nickname && (
                      <CustomFieldLabel
                        customField="Nickname"
                        fieldValue={`${provider?.nickname}`}
                        fieldValueBgColor="background.teal100"
                        fieldValueTextColor="text.primary"
                      />
                    )}
                    {provider?.npi && (
                      <CustomFieldLabel
                        customField="NPI"
                        fieldValue={`${provider?.npi}`}
                        fieldValueBgColor="background.teal100"
                        fieldValueTextColor="text.primary"
                      />
                    )}
                    {newPatientProps?.fieldValue && (
                      <CustomFieldLabel
                        customField="New Patient"
                        fieldValue={newPatientProps?.fieldValue}
                        fieldValueBgColor={
                          newPatientProps?.fieldValueBgColor
                        }
                        fieldValueTextColor={
                          newPatientProps?.fieldValueTextColor
                        }
                        hideCustomFieldLabel={true}
                      />
                    )}
                    <CustomFieldLabel
                      customField="Treats"
                      fieldValue={`${
                        getCustomFieldValue(
                          provider?.customFields,
                          'Age',
                        ) || 'Unknown'
                      }`}
                      fieldValueBgColor="background.teal100"
                      fieldValueTextColor="text.primary"
                    />
                    <CustomFieldLabel
                      customField="Specialty"
                      fieldValue={`${
                        provider?.specialty || 'Unknown'
                      }`}
                      fieldValueBgColor="background.blue100"
                      fieldValueTextColor="text.primary"
                    />
                  </Flex>
                </Flex>
                {/* Athena Department */}
                <Skeleton
                  isLoaded={!loadingProvider && !loadingPractice}
                >
                  {athenaDepartments.length > 0 && (
                    <Flex
                      align="flex-start"
                      justify="space-between"
                      p="0.375rem 1rem"
                      bg="white"
                      gap="1.2rem"
                      mb="0"
                    >
                      <Text
                        fontSize="0.875rem"
                        fontWeight="400"
                        lineHeight={'1.25rem'}
                        color="text.primary"
                        flexShrink={0}
                        minWidth="8rem"
                        pt="0.125rem"
                      >
                        Athena Department
                      </Text>
                      <Box
                        width="fit-content"
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        gap="0.125rem"
                      >
                        {athenaDepartments.map(
                          (
                            {
                              departmentName,
                              departmentLink,
                              practiceName,
                              practiceLocation,
                            },
                            index,
                          ) => {
                            // Safe practice lookup
                            const matchedPractice = practiceName
                              ? practices?.find(
                                  (p) => p.name === practiceName,
                                )
                              : undefined;

                            const practiceAddress = matchedPractice
                              ? constructPracticeAddress(
                                  matchedPractice,
                                )
                              : undefined;

                            return (
                              <React.Fragment
                                key={`${practiceName || 'unknown'}-${
                                  departmentName || 'no-dept'
                                }-${index}`}
                              >
                                {index > 0 && (
                                  <Divider borderColor="gray.200" />
                                )}
                                <AthenaDepartmentLink
                                  departmentName={departmentName}
                                  departmentLink={departmentLink}
                                  practiceName={practiceName}
                                  practiceLocation={practiceLocation}
                                  practiceId={matchedPractice?.id}
                                  practiceAddress={practiceAddress}
                                />
                              </React.Fragment>
                            );
                          },
                        )}
                      </Box>
                    </Flex>
                  )}
                  {/* Cerner Fields */}
                  <Box width="100%" mt="1rem">
                    <Text
                      fontSize="0.875rem"
                      fontWeight="700"
                      lineHeight="1.25rem"
                      color="text.secondary"
                      mb="0.5rem"
                    >
                      Cerner Information
                    </Text>
                    <Flex direction="column" gap="1rem">
                      {cernerPoolEntries.length > 0 ? (
                        <Flex
                          align="flex-start"
                          gap="0.75rem"
                          minWidth={{ base: '0', md: '15rem' }}
                        >
                          <Text
                            color="gray.600"
                            fontSize="0.75rem"
                            fontWeight="700"
                            lineHeight="1.125rem"
                            pt="0.125rem"
                            minWidth="6.5rem"
                          >
                            Cerner Pool
                          </Text>
                          <Box
                            width="fit-content"
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                            gap="0.25rem"
                          >
                            {cernerPoolEntries.map((entry, index) => (
                              <React.Fragment
                                key={`${entry.practiceName || 'unknown'}-${
                                  entry.value
                                }-${index}`}
                              >
                                {index > 0 && (
                                  <Divider borderColor="gray.200" />
                                )}
                                <VStack
                                  align="flex-end"
                                  spacing={1}
                                  maxWidth={{ base: '100%', md: '28rem' }}
                                >
                                  {(entry.practiceName ||
                                    entry.practiceLocation) && (
                                    <Text
                                      fontSize="0.75rem"
                                      color="gray.600"
                                      textAlign="right"
                                    >
                                      {entry.practiceName}
                                      {entry.practiceLocation
                                        ? ` • ${entry.practiceLocation}`
                                        : ''}
                                    </Text>
                                  )}
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    px="0.5rem"
                                    py="0.25rem"
                                    borderRadius="0.25rem"
                                    bg="background.blue50"
                                  >
                                    <Text
                                      color="gray.800"
                                      fontSize="0.75rem"
                                      fontWeight="500"
                                      lineHeight="1rem"
                                      whiteSpace="normal"
                                    >
                                      {entry.value}
                                    </Text>
                                  </Box>
                                </VStack>
                              </React.Fragment>
                            ))}
                          </Box>
                        </Flex>
                      ) : (
                        <CustomFieldLabel
                          customField="Cerner Pool"
                          fieldValue={
                            getCustomFieldValue(
                              provider?.customFields,
                              'Cerner-pool',
                            ) || 'Not specified'
                          }
                          fieldValueBgColor="background.blue50"
                          enableTruncate={false}
                        />
                      )}
                      <CustomFieldLabel
                        customField="Cerner Encounter Location"
                        fieldValue={
                          getCustomFieldValue(
                            provider?.customFields,
                            'Cerner-location',
                          ) || 'Not specified'
                        }
                        fieldValueBgColor="background.blue50"
                      />
                    </Flex>
                  </Box>
                </Skeleton>

                <HStack align={'flex-start'} gap={'0.5rem'} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <DailyAlertsCard provider={provider} />
                  <OfficeNotesCard
                    provider={provider}
                    practices={
                      provider?.relatedPractices?.length
                        ? provider.relatedPractices.map(
                            (practice) => ({
                              id: practice.id,
                              name: practice.name,
                            }),
                          )
                        : []
                    }
                  />
                </HStack>
                {provider?.id && (
                  <ProviderAbsencesCard
                    providerId={provider.id}
                    practices={memoizedPractices}
                  />
                )}
                {/* Triage Triggers and Scheduling Notes Row - Below Absences */}
                <Box width="100%" mt="1rem">
                  {/* Triage Triggers (Specialist only) */}
                  {providerTypeCanon === 'Specialist' && (
                    <Box width="100%">
                      <RichTextCustomFieldCard
                        title="Triage Triggers"
                        customField={
                          provider?.customFields?.[
                            'Triage-Triggers'
                          ] as CustomFieldValue[] | undefined
                        }
                        backgroundColor="yellow.100"
                        gradientColor="#fefce8"
                        buttonColor="yellow.600"
                        buttonHoverColor="yellow.50"
                        emptyStateMessage="No triage triggers available"
                        alwaysExpanded
                      />
                      {/* Fallback display when RichTextCustomFieldCard returns null */}
                      {(!provider?.customFields?.[
                        'Triage-Triggers'
                      ] ||
                        !Array.isArray(
                          provider?.customFields?.['Triage-Triggers'],
                        ) ||
                        provider?.customFields?.['Triage-Triggers']
                          .length === 0) && (
                        <Box width="100%">
                          <Text
                            fontSize="0.875rem"
                            fontWeight="700"
                            lineHeight="1.25rem"
                            color="text.secondary"
                            mb="0.5rem"
                          >
                            Triage Triggers
                          </Text>
                          <Text
                            fontSize="sm"
                            color="text.secondary"
                            fontStyle="italic"
                          >
                            No triage triggers available
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Scheduling Notes (SPECIALTY ONLY) */}
                  {providerTypeCanon === 'Specialist' && (
                    <Box width="100%" mt="1rem">
                      <RichTextCustomFieldCard
                        title="Scheduling Notes"
                        customField={
                          provider?.customFields?.[
                            'Scheduling-Notes'
                          ] as CustomFieldValue[] | undefined
                        }
                        backgroundColor="purple.100"
                        gradientColor="#f3e8ff"
                        buttonColor="purple.600"
                        buttonHoverColor="purple.50"
                        emptyStateMessage="No scheduling notes available"
                      />
                      {/* Fallback display when RichTextCustomFieldCard returns null */}
                      {(!provider?.customFields?.[
                        'Scheduling-Notes'
                      ] ||
                        !Array.isArray(
                          provider?.customFields?.[
                            'Scheduling-Notes'
                          ],
                        ) ||
                        provider?.customFields?.['Scheduling-Notes']
                          .length === 0) && (
                        <Box width="100%">
                          <Text
                            fontSize="0.875rem"
                            fontWeight="700"
                            lineHeight="1.25rem"
                            color="text.secondary"
                            mb="0.5rem"
                          >
                            Scheduling Notes
                          </Text>
                          <Text
                            fontSize="sm"
                            color="text.secondary"
                            fontStyle="italic"
                          >
                            No scheduling notes available
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                <HStack
                  align={'flex-start'}
                  gap={'0.5rem'}
                  minWidth={0}
                  flexWrap={{ base: 'wrap', md: 'nowrap' }}
                >
                  {/* Treatments Column */}
                  <Box flex={{ base: '1 1 100%', md: '1' }} minWidth={0} overflow="hidden">
                    <Text
                      mt="0.75rem"
                      fontSize={'0.875rem'}
                      fontWeight={700}
                      lineHeight={'120%'}
                      color="text.secondary"
                      mb="0.5rem"
                    >
                      Treatments
                    </Text>
                    <Box
                      border="1px solid"
                      borderColor={'border.subtle'}
                      borderRadius={'0.25rem'}
                      as="section"
                      aria-label="Scheduling"
                    >
                      {treatmentFieldsToShow.map((service, index) => (
                        <Flex
                          key={service.name}
                          align="flex-start"
                          justify="space-between"
                          p="0.5rem 1rem"
                          bg={
                            index % 2 === 0
                              ? 'white'
                              : 'background.secondary'
                          }
                          gap="1.2rem"
                        >
                          <Text
                            fontSize="0.875rem"
                            fontWeight="400"
                            lineHeight={'1.25rem'}
                            color="text.primary"
                            flexShrink={0}
                          >
                            {getServiceLabel(service.name)}
                          </Text>
                          {(() => {
                            const d = (service.detail || '')
                              .trim()
                              .toLowerCase();
                            if (d === 'yes' || d === 'no') {
                              return (
                                <Flex
                                  align="center"
                                  justify="center"
                                  gap="0.5rem"
                                >
                                  <Flex
                                    bg={
                                      d === 'yes'
                                        ? 'green.500'
                                        : 'red.500'
                                    }
                                    boxSize="1.25rem"
                                    borderRadius="50%"
                                    align="center"
                                    justify="center"
                                  >
                                    {d === 'yes' ? (
                                      <CheckIcon
                                        color="white"
                                        width="0.75rem"
                                        height="0.75rem"
                                      />
                                    ) : (
                                      <CloseIcon
                                        color="white"
                                        width="0.75rem"
                                        height="0.75rem"
                                      />
                                    )}
                                  </Flex>
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color={
                                      d === 'yes'
                                        ? 'green.500'
                                        : 'red.500'
                                    }
                                  >
                                    {d === 'yes' ? 'Yes' : 'No'}
                                  </Text>
                                </Flex>
                              );
                            }
                            if (
                              service.isRichText &&
                              service.detail
                            ) {
                              return (
                                <Box
                                  fontSize="0.875rem"
                                  fontWeight="700"
                                  color="text.primary"
                                  lineHeight="1.25rem"
                                  wordBreak="break-word"
                                  textAlign="right"
                                  minWidth={0}
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(
                                      service.detail,
                                    ),
                                  }}
                                />
                              );
                            }
                            return (
                              <Text
                                fontSize="0.875rem"
                                fontWeight="700"
                                color="text.primary"
                                lineHeight="1.25rem"
                                wordBreak="break-word"
                                whiteSpace="normal"
                                textAlign="right"
                                minWidth={0}
                              >
                                {service.detail || '-'}
                              </Text>
                            );
                          })()}
                        </Flex>
                      ))}
                    </Box>
                  </Box>
                  {/* Scheduling Column */}
                  <Box flex={{ base: '1 1 100%', md: '1' }} minWidth={0} overflow="hidden">
                    <Text
                      mt="0.75rem"
                      fontSize={'0.875rem'}
                      fontWeight={700}
                      lineHeight={'120%'}
                      color="text.secondary"
                      mb="0.5rem"
                    >
                      Scheduling
                    </Text>
                    <Box
                      border="1px solid"
                      borderColor={'border.subtle'}
                      borderRadius={'0.25rem'}
                    >
                      {schedulingFieldsToShow.map(
                        (service, index) => (
                          <Flex
                            key={service.name}
                            direction={
                              service.name === 'Additional-Location-Days'
                                ? 'column'
                                : 'row'
                            }
                            align={
                              service.name === 'Additional-Location-Days'
                                ? 'stretch'
                                : 'flex-start'
                            }
                            justify={
                              service.name === 'Additional-Location-Days'
                                ? 'flex-start'
                                : 'space-between'
                            }
                            p="0.5rem 1rem"
                            bg={
                              index % 2 === 0
                                ? 'white'
                                : 'background.secondary'
                            }
                            gap={
                              service.name === 'Additional-Location-Days'
                                ? '0.25rem'
                                : '1.2rem'
                            }
                          >
                            <Text
                              fontSize="0.875rem"
                              fontWeight="400"
                              lineHeight={'1.25rem'}
                              color="text.primary"
                              flexShrink={0}
                            >
                              {getServiceLabel(service.name)}
                            </Text>
                            {service.name === 'Medicare-Wellness' ? (
                              <ExtractMedicareWellness
                                customField={
                                  provider?.customFields[
                                    'Medicare-Wellness'
                                  ]
                                }
                              />
                            ) : service.name === 'Cross-Coverage' ? (
                              <Flex align="center" gap="0.5rem">
                                <CrossCoverageAllApptsBadge
                                  customField={
                                    provider?.customFields[
                                      'Cross-Coverage'
                                    ]
                                  }
                                />
                              </Flex>
                            ) : service.name ===
                              'Cross-Coverage-Sick' ? (
                              <CrossCoverageSickBadge
                                scope="clinic"
                                customField={
                                  provider?.customFields[
                                    'Cross-Coverage-Sick'
                                  ] as CustomFieldValue[] | undefined
                                }
                              />
                            ) : service.name ===
                              'Cross-Coverage-for-Sick-Market' ? (
                              <CrossCoverageSickBadge
                                scope="market"
                                customField={
                                  provider?.customFields[
                                    'Cross-Coverage-for-Sick-Market'
                                  ] as CustomFieldValue[] | undefined
                                }
                              />
                            ) : service.name ===
                              'Closest-Urgent-Care' ? (
                              <ClosestUrgentCareBadge
                                customField={
                                  provider?.customFields[
                                    'Closest-Urgent-Care'
                                  ] as CustomFieldValue[] | undefined
                                }
                              />
                            ) : service.name === 'New-Telehealth' ? (
                              (() => {
                                const value = getCustomFieldValue(
                                  provider?.customFields,
                                  'New-Telehealth',
                                );
                                if (value) {
                                  const isYes =
                                    value.toLowerCase() === 'yes';
                                  const isNo =
                                    value.toLowerCase() === 'no';

                                  if (isYes || isNo) {
                                    return (
                                      <Flex
                                        align="center"
                                        justify="center"
                                        gap="0.5rem"
                                      >
                                        <Flex
                                          bg={
                                            isYes
                                              ? 'green.500'
                                              : 'red.500'
                                          }
                                          boxSize="1.25rem"
                                          borderRadius="50%"
                                          align="center"
                                          justify="center"
                                        >
                                          {isYes ? (
                                            <CheckIcon
                                              color="white"
                                              width="0.75rem"
                                              height="0.75rem"
                                            />
                                          ) : (
                                            <CloseIcon
                                              color="white"
                                              width="0.75rem"
                                              height="0.75rem"
                                            />
                                          )}
                                        </Flex>
                                        <Text
                                          fontSize="0.875rem"
                                          fontWeight="700"
                                          color={
                                            isYes
                                              ? 'green.500'
                                              : 'red.500'
                                          }
                                        >
                                          {isYes ? 'Yes' : 'No'}
                                        </Text>
                                      </Flex>
                                    );
                                  }
                                }
                                return (
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight="1.25rem"
                                    wordBreak="break-word"
                                    whiteSpace="normal"
                                    textAlign="right"
                                    minWidth={0}
                                  >
                                    -
                                  </Text>
                                );
                              })()
                            ) : service.name === 'Telehealth' ? (
                              (() => {
                                const field =
                                  provider?.customFields?.[
                                    'Telehealth'
                                  ];
                                if (
                                  field &&
                                  Array.isArray(field) &&
                                  field.length > 0
                                ) {
                                  const value = getCustomFieldValue(
                                    provider?.customFields,
                                    'Telehealth',
                                  );
                                  const details = String(
                                    field[0]?.metadata
                                      ?.telehealthDetails || '',
                                  );
                                  const isYes = value
                                    .toLowerCase()
                                    .startsWith('yes');
                                  const isNo =
                                    value.toLowerCase() === 'no';

                                  if (isYes || isNo) {
                                    return (
                                      <Flex
                                        direction="column"
                                        gap="0.25rem"
                                      >
                                        <Flex
                                          align="center"
                                          justify="center"
                                          gap="0.5rem"
                                        >
                                          <Flex
                                            bg={
                                              isYes
                                                ? 'green.500'
                                                : 'red.500'
                                            }
                                            boxSize="1.25rem"
                                            borderRadius="50%"
                                            align="center"
                                            justify="center"
                                          >
                                            {isYes ? (
                                              <CheckIcon
                                                color="white"
                                                width="0.75rem"
                                                height="0.75rem"
                                              />
                                            ) : (
                                              <CloseIcon
                                                color="white"
                                                width="0.75rem"
                                                height="0.75rem"
                                              />
                                            )}
                                          </Flex>
                                          <Text
                                            fontSize="0.875rem"
                                            fontWeight="700"
                                            color={
                                              isYes
                                                ? 'green.500'
                                                : 'red.500'
                                            }
                                          >
                                            {isYes ? 'Yes' : 'No'}
                                          </Text>
                                        </Flex>
                                        {isYes && details && (
                                          <Text
                                            fontSize="0.75rem"
                                            color="gray.600"
                                            fontStyle="italic"
                                            textAlign="center"
                                          >
                                            {details}
                                          </Text>
                                        )}
                                      </Flex>
                                    );
                                  }
                                }
                                return (
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight="1.25rem"
                                    wordBreak="break-word"
                                    whiteSpace="normal"
                                    textAlign="right"
                                    minWidth={0}
                                  >
                                    -
                                  </Text>
                                );
                              })()
                            ) : service.name ===
                              'Out-Of-State-Telehealth' ? (
                              (() => {
                                const value = getCustomFieldValue(
                                  provider?.customFields,
                                  'Out-Of-State-Telehealth',
                                );
                                if (value) {
                                  const isYes =
                                    value.toLowerCase() === 'yes';
                                  const isNo =
                                    value.toLowerCase() === 'no';

                                  if (isYes || isNo) {
                                    return (
                                      <Flex
                                        align="center"
                                        justify="center"
                                        gap="0.5rem"
                                      >
                                        <Flex
                                          bg={
                                            isYes
                                              ? 'green.500'
                                              : 'red.500'
                                          }
                                          boxSize="1.25rem"
                                          borderRadius="50%"
                                          align="center"
                                          justify="center"
                                        >
                                          {isYes ? (
                                            <CheckIcon
                                              color="white"
                                              width="0.75rem"
                                              height="0.75rem"
                                            />
                                          ) : (
                                            <CloseIcon
                                              color="white"
                                              width="0.75rem"
                                              height="0.75rem"
                                            />
                                          )}
                                        </Flex>
                                        <Text
                                          fontSize="0.875rem"
                                          fontWeight="700"
                                          color={
                                            isYes
                                              ? 'green.500'
                                              : 'red.500'
                                          }
                                        >
                                          {isYes ? 'Yes' : 'No'}
                                        </Text>
                                      </Flex>
                                    );
                                  }
                                }
                                return (
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight="1.25rem"
                                    wordBreak="break-word"
                                    whiteSpace="normal"
                                    textAlign="right"
                                    minWidth={0}
                                  >
                                    -
                                  </Text>
                                );
                              })()
                            ) : service.name === 'Takes-Walk-Ins' ? (
                              (() => {
                                const value = getCustomFieldValue(
                                  provider?.customFields,
                                  'Takes-Walk-Ins',
                                );
                                if (value) {
                                  const isYes =
                                    value.toLowerCase() === 'yes';
                                  const isNo =
                                    value.toLowerCase() === 'no';

                                  if (isYes || isNo) {
                                    return (
                                      <Flex
                                        align="center"
                                        justify="center"
                                        gap="0.5rem"
                                      >
                                        <Flex
                                          bg={
                                            isYes
                                              ? 'green.500'
                                              : 'red.500'
                                          }
                                          boxSize="1.25rem"
                                          borderRadius="50%"
                                          align="center"
                                          justify="center"
                                        >
                                          {isYes ? (
                                            <CheckIcon
                                              color="white"
                                              width="0.75rem"
                                              height="0.75rem"
                                            />
                                          ) : (
                                            <CloseIcon
                                              color="white"
                                              width="0.75rem"
                                              height="0.75rem"
                                            />
                                          )}
                                        </Flex>
                                        <Text
                                          fontSize="0.875rem"
                                          fontWeight="700"
                                          color={
                                            isYes
                                              ? 'green.500'
                                              : 'red.500'
                                          }
                                        >
                                          {isYes ? 'Yes' : 'No'}
                                        </Text>
                                      </Flex>
                                    );
                                  }
                                }
                                return (
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight="1.25rem"
                                    wordBreak="break-word"
                                    whiteSpace="normal"
                                    textAlign="right"
                                    minWidth={0}
                                  >
                                    -
                                  </Text>
                                );
                              })()
                            ) : service.name ===
                              'Cross-Coverage-All-Appts' ? (
                              <CrossCoverageAllApptsBadge
                                customField={
                                  Array.isArray(
                                    provider?.customFields[
                                      'Cross-Coverage-All-Appts'
                                    ],
                                  )
                                    ? (provider?.customFields[
                                        'Cross-Coverage-All-Appts'
                                      ] as CustomFieldValue[])
                                    : undefined
                                }
                              />
                            ) : service.name ===
                              'Supervision-Physician' ? (
                              <Flex direction="column" gap="0.25rem">
                                {(() => {
                                  const customField =
                                    provider?.customFields[
                                      'Supervision-Physician'
                                    ];
                                  if (
                                    customField &&
                                    Array.isArray(customField) &&
                                    customField[0]?.metadata
                                      ?.providers
                                  ) {
                                    const providers = customField[0]
                                      .metadata.providers as {
                                      providerId: string;
                                      providerLabel: string;
                                    }[];
                                    if (providers.length > 0) {
                                      return (
                                        <>
                                          <Text
                                            fontSize="0.875rem"
                                            fontWeight="700"
                                            color="text.primary"
                                            lineHeight="1.25rem"
                                          >
                                            {service.detail ||
                                              supervisionRoleLabel}
                                          </Text>
                                          <HStack
                                            spacing={1}
                                            flexWrap="wrap"
                                          >
                                            {providers.map(
                                              (
                                                supervisingProvider,
                                              ) => (
                                                <Tag
                                                  key={
                                                    supervisingProvider.providerId
                                                  }
                                                  size="sm"
                                                  colorScheme="blue"
                                                  borderRadius="full"
                                                  variant="outline"
                                                  cursor="pointer"
                                                  onClick={() =>
                                                    navigate(
                                                      `/providers/${supervisingProvider.providerId}`,
                                                    )
                                                  }
                                                  _hover={{
                                                    bg: 'blue.50',
                                                    borderColor:
                                                      'blue.400',
                                                  }}
                                                  transition="all 0.15s ease-in-out"
                                                  fontSize="0.75rem"
                                                  fontWeight="500"
                                                >
                                                  <TagLabel>
                                                    {
                                                      supervisingProvider.providerLabel
                                                    }
                                                  </TagLabel>
                                                </Tag>
                                              ),
                                            )}
                                          </HStack>
                                        </>
                                      );
                                    }
                                  }
                                  if (
                                    service.isRichText &&
                                    service.detail
                                  ) {
                                    return (
                                      <Text
                                        fontSize="0.875rem"
                                        fontWeight="700"
                                        color="text.primary"
                                        lineHeight="1.25rem"
                                        wordBreak="break-word"
                                        textAlign="right"
                                        minWidth={0}
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeHtml(
                                            service.detail,
                                          ),
                                        }}
                                      />
                                    );
                                  }
                                  return (
                                    <Text
                                      fontSize="0.875rem"
                                      fontWeight="700"
                                      color="text.primary"
                                      lineHeight="1.25rem"
                                      wordBreak="break-word"
                                      whiteSpace="normal"
                                      textAlign="right"
                                      minWidth={0}
                                    >
                                      {service.detail || '-'}
                                    </Text>
                                  );
                                })()}
                              </Flex>
                            ) : (
                              (() => {
                                const d = (service.detail || '')
                                  .trim()
                                  .toLowerCase();

                                // Check if it's a simple Yes/No value
                                if (d === 'yes' || d === 'no') {
                                  return (
                                    <Flex
                                      align="center"
                                      justify="center"
                                      gap="0.5rem"
                                    >
                                      <Flex
                                        bg={
                                          d === 'yes'
                                            ? 'green.500'
                                            : 'red.500'
                                        }
                                        boxSize="1.25rem"
                                        borderRadius="50%"
                                        align="center"
                                        justify="center"
                                      >
                                        {d === 'yes' ? (
                                          <CheckIcon
                                            color="white"
                                            width="0.75rem"
                                            height="0.75rem"
                                          />
                                        ) : (
                                          <CloseIcon
                                            color="white"
                                            width="0.75rem"
                                            height="0.75rem"
                                          />
                                        )}
                                      </Flex>
                                      <Text
                                        fontSize="0.875rem"
                                        fontWeight="700"
                                        color={
                                          d === 'yes'
                                            ? 'green.500'
                                            : 'red.500'
                                        }
                                      >
                                        {d === 'yes' ? 'Yes' : 'No'}
                                      </Text>
                                    </Flex>
                                  );
                                }

                                // Check if it's a Yes with details (e.g., "Yes: details")
                                if (
                                  d.startsWith('yes:') ||
                                  d.startsWith('yes: ')
                                ) {
                                  const details =
                                    service.detail.split(': ')[1];
                                  return (
                                    <Flex
                                      direction="column"
                                      gap="0.25rem"
                                    >
                                      <Flex
                                        align="center"
                                        justify="center"
                                        gap="0.5rem"
                                      >
                                        <Flex
                                          bg="green.500"
                                          boxSize="1.25rem"
                                          borderRadius="50%"
                                          align="center"
                                          justify="center"
                                        >
                                          <CheckIcon
                                            color="white"
                                            width="0.75rem"
                                            height="0.75rem"
                                          />
                                        </Flex>
                                      </Flex>
                                      {details && (
                                        <Text
                                          fontSize="0.75rem"
                                          color="gray.600"
                                          fontStyle="italic"
                                          textAlign="center"
                                        >
                                          {details}
                                        </Text>
                                      )}
                                    </Flex>
                                  );
                                }
                                if (
                                  service.isRichText &&
                                  service.detail
                                ) {
                                  return (
                                    <Text
                                      fontSize="0.875rem"
                                      fontWeight="700"
                                      color="text.primary"
                                      lineHeight={'1.25rem'}
                                      wordBreak="break-word"
                                      textAlign={
                                        service.name ===
                                        'Additional-Location-Days'
                                          ? 'left'
                                          : 'right'
                                      }
                                      minWidth={0}
                                      width={
                                        service.name ===
                                        'Additional-Location-Days'
                                          ? '100%'
                                          : undefined
                                      }
                                      dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(
                                          service.detail,
                                        ),
                                      }}
                                    />
                                  );
                                }
                                return (
                                  <Text
                                    fontSize="0.875rem"
                                    fontWeight="700"
                                    color="text.primary"
                                    lineHeight={'1.25rem'}
                                    wordBreak="break-word"
                                    whiteSpace="normal"
                                    textAlign={
                                      service.name ===
                                      'Additional-Location-Days'
                                        ? 'left'
                                        : 'right'
                                    }
                                    minWidth={0}
                                    width={
                                      service.name ===
                                      'Additional-Location-Days'
                                        ? '100%'
                                        : undefined
                                    }
                                  >
                                    {service.detail || '-'}
                                  </Text>
                                );
                              })()
                            )}
                          </Flex>
                        ),
                      )}
                    </Box>
                  </Box>
                </HStack>
                <Skeleton isLoaded={!loadingPlans}>
                  {practices && (
                    <>
                      <PlanCardComponent
                        key={`plans-${refreshKey}-${plans.length}-${plans[0]?.plans?.[0]?.minimumAge}-${plans[0]?.plans?.[0]?.maximumAge}`}
                        plans={plans}
                        practiceId={practices[0]?.id}
                        practiceName={practices[0]?.name}
                        onEditInsurance={
                          canEditInsurance() || isAdmin()
                            ? () => setIsInsuranceModalOpen(true)
                            : undefined
                        }
                        onAddNewPlan={
                          canEditInsurance() || isAdmin()
                            ? () => setIsAddPlanModalOpen(true)
                            : undefined
                        }
                      />
                    </>
                  )}
                </Skeleton>
              </VStack>
            </Stack>
          </Flex>
        </Stack>

        <Modal
          isOpen={isTerminateModalOpen}
          onClose={() => {
            setIsTerminateModalOpen(false);
            setSelectedPracticeIds([]);
            setSelectedPracticeId('');
            setTerminationDate('');
            setDisplayDate('');
          }}
          size="md"
          isCentered
        >
          <ModalOverlay
            bg="blackAlpha.300"
            backdropFilter="blur(10px)"
          />
          <ModalContent
            borderRadius="xl"
            boxShadow="2xl"
            maxW="90vw"
            maxH="90vh"
          >
            <ModalHeader
              borderBottom="1px solid"
              borderColor="gray.200"
              pb="1rem"
              bg="red.50"
              borderTopRadius="xl"
            >
              <Text fontSize="1.25rem" fontWeight="600">
                Terminate Provider
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p="2rem">
              <VStack spacing="1.5rem" align="stretch">
                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Select Practice(s){' '}
                    <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Choose one or more practices where this provider
                    will be terminated. You can terminate at multiple
                    practices at once.
                  </Text>
                  <VStack
                    align="stretch"
                    spacing={2}
                    p={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="white"
                    maxH="200px"
                    overflowY="auto"
                  >
                    {provider?.relatedPractices?.length === 0 ? (
                      <Text
                        fontSize="0.875rem"
                        color="gray.500"
                        fontStyle="italic"
                      >
                        No practices available
                      </Text>
                    ) : (
                      provider?.relatedPractices?.map((practice) => (
                        <Checkbox
                          key={practice.id}
                          isChecked={selectedPracticeIds.includes(
                            practice.id,
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPracticeIds((prev) => [
                                ...prev,
                                practice.id,
                              ]);
                            } else {
                              setSelectedPracticeIds((prev) =>
                                prev.filter(
                                  (id) => id !== practice.id,
                                ),
                              );
                            }
                          }}
                          colorScheme="red"
                          size="md"
                        >
                          <Text fontSize="0.875rem" color="gray.700">
                            {formatPracticeNameWithTablespace(
                              practice,
                            )}
                          </Text>
                        </Checkbox>
                      ))
                    )}
                    {selectedPracticeIds.length > 0 && (
                      <Text
                        fontSize="0.75rem"
                        color="red.600"
                        mt={2}
                        fontWeight="500"
                      >
                        {selectedPracticeIds.length} practice
                        {selectedPracticeIds.length > 1
                          ? 's'
                          : ''}{' '}
                        selected
                      </Text>
                    )}
                  </VStack>
                </Box>

                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Termination Date{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Select when the provider should be terminated.
                  </Text>
                  <Input
                    type="date"
                    value={terminationDate}
                    onChange={(e) =>
                      setTerminationDate(e.target.value)
                    }
                    size="md"
                    borderRadius="md"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{
                      borderColor: 'red.500',
                      boxShadow: '0 0 0 1px #e53e3e',
                    }}
                  />

                  {/* UX Helper Text */}
                  {terminationDate && (
                    <Box
                      mt="0.5rem"
                      p="0.75rem"
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Text fontSize="0.75rem" color="gray.600">
                        {(() => {
                          const selectedDate = new Date(
                            terminationDate,
                          );
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          selectedDate.setHours(0, 0, 0, 0);

                          if (selectedDate < today) {
                            return (
                              <Text color="red.600" fontWeight="500">
                                Provider will be terminated
                                immediately (past date selected)
                              </Text>
                            );
                          } else if (
                            selectedDate.getTime() === today.getTime()
                          ) {
                            return (
                              <Text
                                color="orange.600"
                                fontWeight="500"
                              >
                                Provider will be terminated today
                              </Text>
                            );
                          } else {
                            return (
                              <Text color="blue.600" fontWeight="500">
                                Provider will be terminated after{' '}
                                {terminationDate}
                              </Text>
                            );
                          }
                        })()}
                      </Text>
                    </Box>
                  )}
                </Box>

                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Display Until Date (Optional)
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Select a date after the termination date to keep
                    the provider visible in the practice. If not set,
                    the provider will be hidden immediately after
                    termination.
                  </Text>
                  <Input
                    type="date"
                    value={displayDate}
                    onChange={(e) => setDisplayDate(e.target.value)}
                    min={terminationDate || undefined}
                    size="md"
                    borderRadius="md"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                    isDisabled={!terminationDate}
                  />

                  {/* UX Helper Text */}
                  {displayDate && terminationDate && (
                    <Box
                      mt="0.5rem"
                      p="0.75rem"
                      bg="blue.50"
                      borderRadius="md"
                    >
                      <Text fontSize="0.75rem" color="blue.700">
                        Provider will remain visible until{' '}
                        <strong>{displayDate}</strong> (terminated on{' '}
                        {terminationDate})
                      </Text>
                    </Box>
                  )}
                  {!terminationDate && (
                    <Text
                      fontSize="0.75rem"
                      color="gray.500"
                      mt="0.5rem"
                      fontStyle="italic"
                    >
                      Please select a termination date first
                    </Text>
                  )}
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter
              borderTop="1px solid"
              borderColor="gray.200"
              pt="1rem"
              bg="red.50"
              borderBottomRadius="xl"
            >
              <HStack spacing="1rem">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsTerminateModalOpen(false);
                    setIsConfirmModalOpen(false);
                    setSelectedPracticeIds([]);
                    setSelectedPracticeId('');
                    setTerminationDate('');
                    setDisplayDate('');
                  }}
                  size="md"
                  borderRadius="md"
                  isDisabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={() => setIsConfirmModalOpen(true)}
                  isDisabled={
                    selectedPracticeIds.length === 0 ||
                    !terminationDate ||
                    isLoading
                  }
                  size="md"
                  borderRadius="md"
                >
                  Continue to Confirm
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          isOpen={isConfirmModalOpen}
          onClose={() => {
            setIsConfirmModalOpen(false);
            setSelectedPracticeIds([]);
            setSelectedPracticeId('');
          }}
          size="md"
          isCentered
        >
          <ModalOverlay
            bg="blackAlpha.300"
            backdropFilter="blur(10px)"
          />
          <ModalContent
            borderRadius="xl"
            boxShadow="2xl"
            maxW="90vw"
            maxH="90vh"
          >
            <ModalHeader
              borderBottom="1px solid"
              borderColor="gray.200"
              pb="1rem"
              bg="red.50"
              borderTopRadius="xl"
            >
              <Text fontSize="1.25rem" fontWeight="600">
                Confirm Termination
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p="2rem">
              <VStack spacing="1.5rem" align="stretch">
                <Box>
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="red.600"
                    mb="1rem"
                  >
                    ⚠️ Are you sure you want to terminate this
                    provider?
                  </Text>

                  <Text
                    fontSize="0.875rem"
                    color="gray.700"
                    mb="0.5rem"
                  >
                    <strong>Provider:</strong>{' '}
                    {provider
                      ? formatProviderName({
                          ...provider,
                          customFields: provider.customFields,
                        })
                      : 'Provider'}
                  </Text>

                  <VStack align="flex-start" spacing={1} mb="0.5rem">
                    <Text
                      fontSize="0.875rem"
                      color="gray.700"
                      fontWeight="600"
                    >
                      Practice
                      {selectedPracticeIds.length > 1 ? 's' : ''}:
                    </Text>
                    {selectedPracticeIds.length > 0 ? (
                      <VStack align="flex-start" spacing={1} pl={4}>
                        {selectedPracticeIds.map((practiceId) => {
                          const practice =
                            provider?.relatedPractices?.find(
                              (p) => p.id === practiceId,
                            );
                          return (
                            <Text
                              key={practiceId}
                              fontSize="0.875rem"
                              color="gray.700"
                            >
                              • {practice?.name || 'Unknown Practice'}
                            </Text>
                          );
                        })}
                      </VStack>
                    ) : selectedPracticeId ? (
                      <Text
                        fontSize="0.875rem"
                        color="gray.700"
                        pl={4}
                      >
                        •{' '}
                        {
                          provider?.relatedPractices?.find(
                            (p) => p.id === selectedPracticeId,
                          )?.name
                        }
                      </Text>
                    ) : null}
                  </VStack>

                  <Text
                    fontSize="0.875rem"
                    color="gray.700"
                    mb="0.5rem"
                  >
                    <strong>Termination Date:</strong>{' '}
                    {terminationDate}
                  </Text>

                  {displayDate && (
                    <Text
                      fontSize="0.875rem"
                      color="gray.700"
                      mb="1rem"
                    >
                      <strong>Display Until:</strong> {displayDate}{' '}
                      (Provider will remain visible until this date)
                    </Text>
                  )}
                  {!displayDate && (
                    <Text
                      fontSize="0.875rem"
                      color="gray.600"
                      mb="1rem"
                      fontStyle="italic"
                    >
                      Provider will be hidden immediately after
                      termination
                    </Text>
                  )}

                  {(() => {
                    const selectedDate = new Date(terminationDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    selectedDate.setHours(0, 0, 0, 0);

                    if (selectedDate < today) {
                      return (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="0.875rem">
                            Provider will be terminated immediately
                          </Text>
                        </Alert>
                      );
                    } else if (
                      selectedDate.getTime() === today.getTime()
                    ) {
                      return (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="0.875rem">
                            Provider will be terminated today
                          </Text>
                        </Alert>
                      );
                    } else {
                      return (
                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="0.875rem">
                            Provider will be terminated after{' '}
                            {terminationDate}
                          </Text>
                        </Alert>
                      );
                    }
                  })()}
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter
              borderTop="1px solid"
              borderColor="gray.200"
              pt="1rem"
              bg="red.50"
              borderBottomRadius="xl"
            >
              <HStack spacing="1rem">
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmModalOpen(false)}
                  size="md"
                  borderRadius="md"
                  isDisabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleTerminateProvider}
                  isLoading={isLoading}
                  loadingText="Terminating..."
                  size="md"
                  borderRadius="md"
                >
                  Confirm Termination
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Insurance Plans Modal */}
        <ProviderInsuranceModal
          isOpen={isInsuranceModalOpen}
          onClose={() => setIsInsuranceModalOpen(false)}
          providerId={provider?.id || ''}
          practiceIds={
            provider?.relatedPractices?.map((p) => p.id) || []
          }
          providerName={
            provider
              ? formatProviderName({
                  ...provider,
                  customFields: provider.customFields,
                })
              : ''
          }
          onSaved={() => {
            // Refresh provider and plans data after insurance changes
            // Reset fetch tracking to allow immediate refresh
            hasFetchedProviderRef.current = false;
            hasFetchedPlansRef.current = false;
            lastFetchTimeRef.current = 0;
            lastFetchPlansTimeRef.current = 0;
            setRefreshKey((prev) => prev + 1); // Force component re-render
            fetchProvider();
            // Single refresh call - no need for multiple delayed calls
            fetchPlans();
          }}
        />

        {/* Add New Insurance Plans Modal */}
        {provider?.id && practices && practices.length > 0 && (
          <AddInsurancePlansModal
            isOpen={isAddPlanModalOpen}
            onClose={() => setIsAddPlanModalOpen(false)}
            providerId={provider.id}
            practiceIds={practices.map((p) => p.id)}
            defaultState={providerStateForModal}
            existingInsuranceIds={existingInsuranceIds}
            onSuccess={() => {
              // Refresh provider and plans data after adding insurance plans
              hasFetchedProviderRef.current = false;
              hasFetchedPlansRef.current = false;
              lastFetchTimeRef.current = 0;
              lastFetchPlansTimeRef.current = 0;
              setRefreshKey((prev) => prev + 1);
              fetchProvider();
              fetchPlans();
            }}
          />
        )}
      </>
    </>
  );
};

export { ProviderDetail };
